import React, { type ReactNode } from 'react';
import { useIntl } from 'react-intl-next';
import { css } from '@compiled/react';

import { Flex, Text } from '@atlaskit/primitives/compiled';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightPrimaryAction,
} from '@atlaskit/spotlight';
import { FadeIn } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';

import { i18n } from './emojiPopoverSpotlightMessages.ts';

const spotlightBodyStyles = css({
	width: '100%',
	maxWidth: '100%',
	overflow: 'hidden',
	wordWrap: 'break-word',
	overflowWrap: 'break-word',
	whiteSpace: 'normal',
});

export interface EmojiPopoverSpotlightWrapperProps {
	children: ReactNode;
	showSpotlight: boolean;
}

const noop = () => {};

export const EmojiPopoverSpotlightWrapper = ({
	children,
	showSpotlight,
}: EmojiPopoverSpotlightWrapperProps) => {
	const { formatMessage } = useIntl();

	if (!showSpotlight) {
		return <>{children}</>;
	}

	return (
		<Flex>
			<PopoverProvider>
				<PopoverTarget>{children}</PopoverTarget>
				<PopoverContent dismiss={noop} done={noop} placement="left-end" isVisible offset={[40, 10]}>
					{(() => {
						const spotlightCard = (
							<SpotlightCard testId="emoji-popover-spotlight">
								<SpotlightHeader>
									<SpotlightHeadline>{formatMessage(i18n.spotlightHeadline)}</SpotlightHeadline>
									<SpotlightControls>
										<SpotlightDismissControl />
									</SpotlightControls>
								</SpotlightHeader>
								<SpotlightBody>
									<div css={spotlightBodyStyles}>
										<Text>{formatMessage(i18n.spotlightBody)}</Text>
									</div>
								</SpotlightBody>
								<SpotlightFooter>
									<SpotlightActions>
										<SpotlightPrimaryAction>
											{formatMessage(i18n.spotlightDone)}
										</SpotlightPrimaryAction>
									</SpotlightActions>
								</SpotlightFooter>
							</SpotlightCard>
						);
						return fg('platform-dst-motion-uplift') ? (
							spotlightCard
						) : (
							<FadeIn entranceDirection="left">
								{(props: any) => <div {...props}>{spotlightCard}</div>}
							</FadeIn>
						);
					})()}
				</PopoverContent>
			</PopoverProvider>
		</Flex>
	);
};
